/* @jsx h */
import { defineComponent, h, PropType } from "vue";
import type { GalleryAsset } from "../../../../api/client";
import type { ShopProfile } from "@lemmonhq/types";
import { RichTextContent, useLocalization } from "@lemmonhq/uikit";

export const RestaurantDetails = defineComponent({
  props: {
    restaurant: {
      type: Object as PropType<ShopProfile>,
      required: true,
    },
    gallery: {
      type: Array as PropType<GalleryAsset[]>,
      required: true,
    },
    reservationEnabled: {
      type: Boolean,
      required: true,
    },
    deliveryEnabled: {
      type: Boolean,
      required: true,
    },
    pickupEnabled: {
      type: Boolean,
      required: true,
    },
    onlineOrderingEnabled: {
      type: Boolean,
      required: true,
    },
    reservationMinGuests: {
      type: String as PropType<string | null>,
      required: true,
    },
    reservationMaxGuests: {
      type: String as PropType<string | null>,
      required: true,
    },
    reservationAdvanceDays: {
      type: String as PropType<string | null>,
      required: true,
    },
  },
  setup(props) {
    const { t } = useLocalization();
    return () => (
      <div class="space-y-12">
        {props.restaurant.description && (
          <section>
            <h2 class="text-2xl font-serif font-light text-gray-900 mb-6">
              {t("customer.restaurant_profile.about")}
            </h2>
            <RichTextContent
              html={props.restaurant.description}
              className="prose-lg text-gray-600 font-light leading-relaxed"
            />
          </section>
        )}

        {(props.reservationEnabled ||
          props.deliveryEnabled ||
          props.pickupEnabled ||
          props.onlineOrderingEnabled) && (
          <section>
            <h2 class="text-2xl font-serif font-light text-gray-900 mb-6">
              {t("customer.restaurant_profile.services")}
            </h2>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
              {props.reservationEnabled && (
                <div class="flex flex-col items-center p-4 border border-gray-200 rounded-lg">
                  <span class="material-symbols-outlined text-2xl text-gray-600 mb-2">
                    calendar_month
                  </span>
                  <span class="text-sm text-gray-700 font-medium">
                    {t("customer.restaurant_profile.reservations")}
                  </span>
                </div>
              )}
              {props.deliveryEnabled && (
                <div class="flex flex-col items-center p-4 border border-gray-200 rounded-lg">
                  <span class="material-symbols-outlined text-2xl text-gray-600 mb-2">
                    local_shipping
                  </span>
                  <span class="text-sm text-gray-700 font-medium">
                    {t("customer.restaurant_profile.delivery")}
                  </span>
                </div>
              )}
              {props.pickupEnabled && (
                <div class="flex flex-col items-center p-4 border border-gray-200 rounded-lg">
                  <span class="material-symbols-outlined text-2xl text-gray-600 mb-2">
                    takeout_dining
                  </span>
                  <span class="text-sm text-gray-700 font-medium">
                    {t("customer.restaurant_profile.pickup")}
                  </span>
                </div>
              )}
              {props.onlineOrderingEnabled && (
                <div class="flex flex-col items-center p-4 border border-gray-200 rounded-lg">
                  <span class="material-symbols-outlined text-2xl text-gray-600 mb-2">
                    smartphone
                  </span>
                  <span class="text-sm text-gray-700 font-medium">
                    {t("customer.restaurant_profile.online_order")}
                  </span>
                </div>
              )}
            </div>
          </section>
        )}

        {props.reservationEnabled && false ? (
          <section>
            <h2 class="text-2xl font-serif font-light text-gray-900 mb-6">
              {t("customer.restaurant_profile.reservation_information")}
            </h2>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              {props.reservationMinGuests && (
                <div class="p-4 border border-gray-200 rounded-lg">
                  <div class="text-sm text-gray-500 mb-1">
                    {t("customer.restaurant_profile.minimum_guests")}
                  </div>
                  <div class="text-xl font-medium text-gray-900">
                    {props.reservationMinGuests}
                  </div>
                </div>
              )}
              {props.reservationMaxGuests && (
                <div class="p-4 border border-gray-200 rounded-lg">
                  <div class="text-sm text-gray-500 mb-1">
                    {t("customer.restaurant_profile.maximum_guests")}
                  </div>
                  <div class="text-xl font-medium text-gray-900">
                    {props.reservationMaxGuests}
                  </div>
                </div>
              )}
              {props.reservationAdvanceDays && (
                <div class="p-4 border border-gray-200 rounded-lg">
                  <div class="text-sm text-gray-500 mb-1">
                    {t("customer.restaurant_profile.book_in_advance")}
                  </div>
                  <div class="text-xl font-medium text-gray-900">
                    {t("customer.restaurant_profile.up_to_days", [
                      props.reservationAdvanceDays ?? "",
                    ])}
                  </div>
                </div>
              )}
            </div>
          </section>
        ) : null}

        {props.gallery.length > 1 && (
          <section>
            <h2 class="text-2xl font-serif font-light text-gray-900 mb-6">
              {t("customer.restaurant_profile.gallery")}
            </h2>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              {props.gallery.slice(1, 7).map((photo, index) => (
                <div
                  key={index}
                  class="aspect-square overflow-hidden rounded-lg bg-gray-100"
                >
                  <img
                    src={photo.url}
                    alt={t("customer.restaurant_profile.restaurant_photo")}
                    class="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    );
  },
});
